import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import DimensionIntro from '../components/DimensionIntro';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useTranslation } from '../i18n/LanguageContext';
import { translateQuestion } from '../i18n/questions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Assessment() {
  const navigate = useNavigate();
  const { t, lang } = useTranslation();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [introDimension, setIntroDimension] = useState(null);
  const [shownIntros, setShownIntros] = useState(new Set());
  const [guestId] = useState(`guest_${Date.now()}`);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/questions`);
      const qs = response.data.questions || [];
      setQuestions(qs);
      setLoading(false);
      // Show intro for the first dimension immediately
      if (qs.length > 0) {
        const first = qs[0].dimension;
        setIntroDimension(first);
        setShownIntros(new Set([first]));
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      toast.error('Failed to load questions');
    }
  };

  // Ordered list of unique dimensions in question order
  const orderedDimensions = useMemo(() => {
    const seen = [];
    questions.forEach((q) => {
      if (!seen.includes(q.dimension)) seen.push(q.dimension);
    });
    return seen;
  }, [questions]);

  const currentQuestion = questions[currentIndex];
  const progress = questions.length ? ((currentIndex + 1) / questions.length) * 100 : 0;
  const isLastQuestion = currentIndex === questions.length - 1;

  const advanceTo = (nextIndex) => {
    if (nextIndex >= questions.length) return;
    const nextDim = questions[nextIndex].dimension;
    if (!shownIntros.has(nextDim)) {
      setShownIntros((prev) => new Set([...prev, nextDim]));
      setIntroDimension(nextDim);
      // currentIndex is set when user clicks Continue on intro
      setCurrentIndex(nextIndex);
    } else {
      setCurrentIndex(nextIndex);
    }
  };

  const handleAnswer = (value) => {
    const newAnswers = { ...answers, [currentQuestion.id]: value };
    setAnswers(newAnswers);

    if (isLastQuestion) {
      submitAssessment(newAnswers);
      return;
    }

    setTimeout(() => advanceTo(currentIndex + 1), 150);
  };

  const submitAssessment = async (finalAnswers) => {
    setSubmitting(true);

    const answerCount = Object.keys(finalAnswers || {}).length;
    if (answerCount === 0) {
      toast.error('Please answer at least one question before submitting.');
      setSubmitting(false);
      return;
    }

    const payload = {
      answers: finalAnswers,
      guest_id: guestId,
      completed_at: new Date().toISOString(),
    };

    const submitOnce = async (useFetch = false) => {
      if (useFetch) {
        const resp = await fetch(`${API}/assessments`, {
          method: 'POST',
          credentials: 'omit',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const text = await resp.text();
        let data;
        try { data = JSON.parse(text); } catch { data = { raw: text }; }
        if (!resp.ok) {
          const err = new Error(data?.detail || `HTTP ${resp.status}`);
          err.response = { status: resp.status, data };
          throw err;
        }
        return { data };
      }
      return await axios.post(`${API}/assessments`, payload, {
        timeout: 20000,
        headers: { 'Content-Type': 'application/json' },
      });
    };

    let response;
    try {
      response = await submitOnce(false);
    } catch (err1) {
      console.warn('[Assessment submit] axios attempt failed:', err1?.message);
      try {
        response = await submitOnce(true);
      } catch (err2) {
        console.error('[Assessment submit] BOTH attempts failed', err1, err2);
        try {
          localStorage.setItem('clinity_assessment_draft', JSON.stringify({
            answers: finalAnswers,
            guest_id: guestId,
            saved_at: new Date().toISOString(),
          }));
        } catch {}
        const err = err2 || err1;
        const backendDetail = err?.response?.data?.detail;
        const status = err?.response?.status;
        const msg = backendDetail
          ? `Couldn't submit: ${backendDetail}`
          : `Submit failed${status ? ` (HTTP ${status})` : ''}. Your answers are saved locally.`;
        toast.error(msg);
        setSubmitting(false);
        return;
      }
    }

    try { localStorage.removeItem('clinity_assessment_draft'); } catch {}
    toast.success(t('assess.submit.success'));
    navigate(`/result/${response.data.assessment_id}`);
  };

  // Apply translation to the current question based on selected language
  const translatedQuestion = useMemo(
    () => translateQuestion(currentQuestion, lang),
    [currentQuestion, lang]
  );

  // Pretty dimension label for the pill, supporting i18n
  const dimensionPill = useMemo(() => {
    if (!currentQuestion) return '';
    const key = currentQuestion.dimension.toLowerCase().replace('-', '_');
    const tk = `dim.${key}`;
    const translated = t(tk);
    return translated !== tk ? translated : currentQuestion.dimension;
  }, [currentQuestion, t, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const getMicrocopy = () => {
    if (progress < 25) return t('assess.micro.start');
    if (progress < 50) return t('assess.micro.quarter');
    if (progress < 75) return t('assess.micro.half');
    if (progress < 95) return t('assess.micro.almost');
    return t('assess.micro.last');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]" data-testid="assessment-loading">
        <div className="spinner w-8 h-8 border-4 border-[#4F46E5] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (submitting) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F8FAFC]" data-testid="assessment-submitting">
        <div className="spinner w-12 h-12 border-4 border-[#4F46E5] border-t-transparent rounded-full mb-4" />
        <p className="text-[#64748B]">{t('assess.loading')}</p>
      </div>
    );
  }

  // Dimension intro overlay (covers screen until user clicks Continue)
  if (introDimension) {
    const sectionIndex = orderedDimensions.indexOf(introDimension) + 1;
    return (
      <DimensionIntro
        dimension={introDimension}
        sectionIndex={sectionIndex}
        totalSections={orderedDimensions.length}
        onContinue={() => setIntroDimension(null)}
      />
    );
  }

  return (
    <div
      className="min-h-screen relative overflow-hidden"
      style={{ backgroundColor: '#F8FAFC' }}
      data-testid="assessment-page"
    >
      <div className="absolute inset-0 pattern-dots z-0" />
      <div
        className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full blur-3xl opacity-30 pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%)' }}
      />

      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-50" data-testid="progress-bar">
        <div className="h-1 bg-[#E2E8F0]">
          <div
            className="h-full bg-gradient-to-r from-[#4F46E5] to-[#6366F1] progress-bar"
            style={{
              width: `${progress}%`,
              boxShadow: '0 0 15px rgba(79, 70, 229, 0.4)',
            }}
          />
        </div>
        <div className="bg-white/90 backdrop-blur-xl border-b border-[#E2E8F0] px-6 py-3 flex items-center justify-between gap-3 shadow-soft">
          <span
            className="text-sm font-bold tracking-tight text-[#0F172A] shrink-0"
            style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.02em' }}
            data-testid="app-name"
          >
            {t('assess.brand')}
          </span>
          <LanguageSwitcher className="shrink-0" />
          <span className="text-xs text-[#94A3B8] text-right font-medium shrink-0 hidden sm:inline" data-testid="progress-step">
            {t('assess.step', { current: currentIndex + 1, total: questions.length })}
          </span>
        </div>
        {/* Mobile-only step indicator under the bar */}
        <div className="sm:hidden bg-white/85 px-6 py-1.5 border-b border-[#E2E8F0] flex justify-between text-[11px] text-[#64748B] font-medium">
          <span data-testid="progress-text">{getMicrocopy()}</span>
          <span data-testid="progress-step-mobile">{t('assess.step', { current: currentIndex + 1, total: questions.length })}</span>
        </div>
      </div>

      {/* Question */}
      <div className="relative z-10 flex items-center justify-center min-h-screen px-6 pt-24 pb-12">
        <div className="w-full max-w-2xl fade-in" key={currentIndex}>
          <div className="text-center mb-8">
            <span className="inline-block px-4 py-2 rounded-full bg-[#EEF2FF] text-[#4F46E5] text-sm font-semibold border border-[#E0E7FF]" data-testid="question-dimension-pill">
              {dimensionPill}
            </span>
          </div>

          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-center mb-12 leading-tight text-[#0F172A]"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="question-text"
          >
            {translatedQuestion?.question}
          </h2>

          <div className="space-y-3">
            {translatedQuestion?.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswer(option.value)}
                className="w-full p-5 rounded-2xl border border-[#E2E8F0] bg-white hover:bg-[#EEF2FF] hover:border-[#6366F1] text-left transition-all hover-scale shadow-soft"
                style={{
                  animationDelay: `${index * 0.05}s`,
                  animation: 'fadeIn 0.3s ease-out forwards',
                  opacity: 0,
                }}
                data-testid={`option-${option.value}`}
              >
                <span className="text-[#0F172A] font-medium">{option.text}</span>
              </button>
            ))}
          </div>

          {currentIndex > 0 && (
            <button
              onClick={() => setCurrentIndex(currentIndex - 1)}
              className="mt-8 text-[#64748B] hover:text-[#4F46E5] text-sm transition-colors mx-auto block font-medium"
              data-testid="back-button"
            >
              {t('assess.previous')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default Assessment;
